import {createTheme} from '@mui/material/styles'
import type {
  PaletteColor,
  SimplePaletteColorOptions,
} from '@mui/material/styles'

const {palette} = createTheme()
const paletteColor = (
  color: string | SimplePaletteColorOptions,
): PaletteColor =>
  palette.augmentColor({
    color: typeof color === 'string' ? {main: color} : color,
  })

const ACTION_INK = '49, 36, 69'
const SECONDARY_INK = '139, 98, 195' // secondary #8b62c3, for violet-tinted hover/selection

/**
 * Customize Material colors
 */
declare module '@mui/material/styles' {
  // Pale wash for chips and hovers. Not `light`: MUI derives that one from
  // `main` and it lands mid-tone, already used as a button hover.
  interface PaletteColor {
    lighter?: string
  }
  interface SimplePaletteColorOptions {
    lighter?: string
  }
  interface Palette {
    shift: {
      day: PaletteColor
      night: PaletteColor
    }
    status: {
      validated: PaletteColor
      inProgress: PaletteColor
      cancelled: PaletteColor
    }
    notice: {
      cancel: string
      info: string
    }
    progress: PaletteColor
    report: {
      done: PaletteColor
      partial: PaletteColor
      notdone: PaletteColor
    }
  }
  interface PaletteOptions {
    shift?: {
      day?: PaletteColor
      night?: PaletteColor
    }
    status?: {
      validated: PaletteColor
      inProgress: PaletteColor
      cancelled: PaletteColor
    }
    notice?: {
      cancel: string
      info: string
    }
    progress?: PaletteColor
    report?: {
      done: PaletteColor
      partial: PaletteColor
      notdone: PaletteColor
    }
  }
}

/**
 * MUI custom theme for T-OPS
 *
 * Single source of truth: palette tokens are pulled from theme/colors.ts.
 * All MUI components automatically inherit these values at runtime via ThemeProvider.
 */
export const theme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      //main: '#00b39f',
      main: '#008576', // try with a more accessible green
      lighter: '#e0f1ee',
    },
    secondary: {
      main: '#8b62c3',
    },
    error: {
      light: '#fdecea',
      main: '#d94c5f',
      dark: '#b3261e',
    },
    warning: {
      light: '#faeeda',
      main: '#e8c89a',
      dark: '#854f0b',
    },
    info: {
      main: '#4f8fcc',
    },
    text: {
      primary: '#4B4453',
      secondary: '#7A7280',
      disabled: '#B3AEB8',
    },
    divider: `rgba(${ACTION_INK}, 0.12)`,
    background: {
      default: '#f6f5f8',
      paper: '#ffffff',
    },
    action: {
      active: `rgba(${ACTION_INK}, 0.54)`,
      hover: `rgba(${SECONDARY_INK}, 0.06)`,
      selected: `rgba(${SECONDARY_INK}, 0.14)`,
      focus: `rgba(${ACTION_INK}, 0.12)`,
      disabled: `rgba(${ACTION_INK}, 0.26)`,
      disabledBackground: `rgba(${ACTION_INK}, 0.12)`,
    },
    shift: {
      day: paletteColor({main: '#F2A93B', light: '#FDF0D5'}),
      night: paletteColor({main: '#5C6BC0', light: '#E8EAF6'}),
    },
    status: {
      validated: paletteColor('#008576'),
      // If we want a validated color different from the primary color
      //validated: paletteColor('#34a878'),
      inProgress: paletteColor('#f0bd74'),
      cancelled: paletteColor({main: '#d94c5f', light: '#F5C8CE'}),
    },
    notice: {
      cancel: '#FAF5CE',
      info: '#B0CDE8',
    },
    progress: paletteColor({
      main: '#cbc2d9',
      light: '#e4dfec',
      lighter: '#efedf3',
    }),
    // Shift-report outcomes sit under the activity chart on the dashboard: an
    // inkier register than status.* so "done" never reads as "validated" nor
    // "notdone" as "cancelled". `partial` is a deliberate neutral (diverging
    // midpoint). `main` fills charts and glyphs; `dark` is the text ink on the
    // `lighter` chip tint, each pair chosen for at least 4.5:1 (12px bold
    // labels), where `main` on the tint reads at barely 2:1.
    report: {
      done: paletteColor({
        main: '#4AA3A2',
        dark: '#1B605F',
        lighter: '#A7E0E0',
      }),
      partial: paletteColor({
        main: '#DAAB3A',
        dark: '#8C561F',
        lighter: '#EEE6D8',
      }),
      notdone: paletteColor({
        main: '#CE6A6B',
        dark: '#7C2D2E',
        lighter: '#EBACA2',
      }),
    },
  },
  typography: {
    fontFamily: 'var(--app-font-family)',
    allVariants: {
      fontFamily: 'var(--app-font-family)',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'var(--app-font-family)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--app-font-family)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--app-font-family)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--app-font-family)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'var(--mui-palette-background-default)',
          fontWeight: 600,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
        disableRipple: true,
      },
    },
  },
})

/** Dashboard colors */
export const chartColors = {
  activityType: {
    WORK_ACTIVITY: theme.palette.warning.dark,
    MOVEMENT_ACTIVITY: theme.palette.secondary.main,
    BUILDING_ACCESS: theme.palette.warning.main,
    ENCLOSURE_ACCESS: theme.palette.primary.main,
  } as Record<string, string>,
}
